import { useEffect, useState } from "react";
import { apiFetch } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash2, Layers, Users2 } from "lucide-react";
import { toast } from "sonner";

// Admins create shared / role-based dashboard templates from the CURRENT layout.
export function TemplateManager({ open, onOpenChange, currentLayout }) {
  const [templates, setTemplates] = useState([]);
  const [roles, setRoles] = useState([]);
  const [name, setName] = useState("");
  const [scope, setScope] = useState("shared");
  const [roleName, setRoleName] = useState("");

  const load = async () => {
    try {
      const [t, r] = await Promise.all([apiFetch("/dashboard-templates"), apiFetch("/roles")]);
      setTemplates(t);
      setRoles(r);
      if (r[0] && !roleName) setRoleName(r[0].name);
    } catch (e) { toast.error(e.message); }
  };
  useEffect(() => { if (open) load(); }, [open]);

  const save = async () => {
    if (!name.trim()) return toast.error("Give the template a name.");
    try {
      await apiFetch("/dashboard-templates", {
        method: "POST",
        body: JSON.stringify({
          name: name.trim(), scope,
          role_name: scope === "role" ? roleName : null,
          layout: currentLayout,
        }),
      });
      toast.success("Template saved from current layout");
      setName("");
      load();
    } catch (e) { toast.error(e.message); }
  };

  const remove = async (id) => {
    try { await apiFetch(`/dashboard-templates/${id}`, { method: "DELETE" }); load(); toast.success("Template deleted"); }
    catch (e) { toast.error(e.message); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="template-manager">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Dashboard templates</DialogTitle>
          <DialogDescription>Save your current layout as a Shared template or assign it to a Role. Users can personalise it without changing the template.</DialogDescription>
        </DialogHeader>

        <div className="space-y-3 rounded-lg border border-border p-4">
          <div>
            <Label className="text-xs">Template name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Reviewer Starter" className="mt-1" data-testid="template-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Type</Label>
              <Select value={scope} onValueChange={setScope}>
                <SelectTrigger className="mt-1" data-testid="template-scope"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="shared">Shared</SelectItem>
                  <SelectItem value="role">Role-based</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {scope === "role" && (
              <div>
                <Label className="text-xs">Role</Label>
                <Select value={roleName} onValueChange={setRoleName}>
                  <SelectTrigger className="mt-1" data-testid="template-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {roles.map((r) => <SelectItem key={r.name} value={r.name}>{r.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <Button className="w-full" onClick={save} data-testid="save-template">Save current layout as template</Button>
        </div>

        <div className="mt-2 max-h-64 space-y-2 overflow-y-auto scrollbar-thin">
          {templates.length === 0 ? (
            <p className="py-4 text-center text-sm text-muted-foreground">No templates yet.</p>
          ) : templates.map((t) => (
            <div key={t.id} className="flex items-center gap-3 rounded-lg border border-border p-3" data-testid={`template-${t.id}`}>
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-secondary text-primary">
                {t.scope === "role" ? <Users2 className="h-4 w-4" /> : <Layers className="h-4 w-4" />}
              </span>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-foreground">{t.name}</div>
                <div className="text-xs text-muted-foreground">{t.scope === "role" ? `Role · ${t.role_name}` : "Shared"}</div>
              </div>
              <button onClick={() => remove(t.id)} className="text-destructive" data-testid={`delete-template-${t.id}`}>
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
